import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import ProcessNode from './ProcessNode';
import ImplementPanel from './ImplementPanel';
import { submitJob } from '../apiClient';

const nodeTypes = { processNode: ProcessNode };

function buildNodesAndEdges(processData, topLiteAgentIds, allLiteAgentIds = []) {
  if (!processData?.steps) return { nodes: [], edges: [] };

  const allLiteIds = allLiteAgentIds.length > 0 ? allLiteAgentIds : topLiteAgentIds;

  // Continuous Lite-Agent numbering (Topic 2): the top 3 (from the "Top Lite
  // Agent Empfehlungen" list) are "Empfohlener Lite Agent #1..3"; every other
  // candidate continues as "Lite Agent #4", "#5"... — never an empty "#".
  const liteRank = {};
  const liteSuggested = {};
  topLiteAgentIds.forEach((id, idx) => { liteRank[id] = idx + 1; liteSuggested[id] = true; });
  let nextRank = topLiteAgentIds.length + 1;
  allLiteIds.forEach(id => {
    if (liteRank[id] == null) { liteRank[id] = nextRank++; liteSuggested[id] = false; }
  });

  const nodes = processData.steps.map((step, i) => {
    const isLiteAgent = allLiteIds.includes(step.id);

    return {
      id: step.id,
      type: 'processNode',
      position: { x: 80 + i * 340, y: 140 + (i % 2 === 0 ? 0 : 70) },
      data: {
        ...step,
        stepNumber: i + 1,
        isLiteAgent,
        liteAgentRank: isLiteAgent ? (liteRank[step.id] ?? null) : null,
        liteAgentSuggested: !!liteSuggested[step.id],
        level: step.level || null,
      },
    };
  });

  const edges = (processData.connections || []).map((conn, i) => ({
    id: `edge-${i}`,
    source: conn.from,
    target: conn.to,
    label: conn.label || '',
    animated: true,
    style: { stroke: '#555', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#555' },
    labelStyle: { fontSize: 11, fill: '#999' },
  }));

  return { nodes, edges };
}

export default function CanvasPhase({ mode, processData, processContext, selectedHA, mappingData, onStartMapping, onExport, onNewProcess, exportState }) {
  const [selectedStep, setSelectedStep] = useState(null);
  const [implementData, setImplementData] = useState({});
  const [loadingImpl, setLoadingImpl] = useState(null);
  const [implError, setImplError] = useState({}); // stepId -> error message

  // Resizable panel state
  const [panelWidth, setPanelWidth] = useState(420);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(420);

  // Top 3 IDs for rank display
  const topLiteAgentIds = useMemo(() => {
    if (mappingData?.topLiteAgents) {
      // Max 3 recommended — must match the summary's top-3 list.
      return mappingData.topLiteAgents.slice(0, 3).map(a => a.stepId);
    }
    if (mappingData?.mappings) {
      return mappingData.mappings
        .filter(m => m.liteAgentFit?.suitable)
        .slice(0, 3)
        .map(m => m.stepId);
    }
    return [];
  }, [mappingData]);

  // ALL suitable Lite Agent IDs (for highlighting)
  const allLiteAgentIds = useMemo(() => {
    if (mappingData?.mappings) {
      return mappingData.mappings
        .filter(m => m.liteAgentFit?.suitable || m.caseType === 'lite-agent')
        .map(m => m.stepId);
    }
    return topLiteAgentIds;
  }, [mappingData, topLiteAgentIds]);

  const initial = useMemo(
    () => buildNodesAndEdges(processData, topLiteAgentIds, allLiteAgentIds),
    [processData, topLiteAgentIds, allLiteAgentIds]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);

  // V02: Re-sync nodes/edges when processData changes (e.g. after edit chat applies changes)
  useEffect(() => {
    const updated = buildNodesAndEdges(processData, topLiteAgentIds, allLiteAgentIds);
    setNodes(updated.nodes);
    setEdges(updated.edges);
  }, [processData, topLiteAgentIds, allLiteAgentIds, setNodes, setEdges]);

  const onConnect = useCallback(
    (params) => setEdges(eds => addEdge({ ...params, animated: true }, eds)),
    [setEdges]
  );

  // Resizable panel handlers
  const handleResizeStart = useCallback((e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, [panelWidth]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      const diff = startX.current - e.clientX;
      const newWidth = Math.min(600, Math.max(300, startWidth.current + diff));
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Fetch (or re-fetch) the implementation for a step.
  const loadImplementation = useCallback(async (step) => {
    const stepMapping = mappingData?.mappings?.find(m => m.stepId === step.id);
    setLoadingImpl(step.id);
    setImplError(prev => ({ ...prev, [step.id]: null }));
    try {
      const data = await submitJob(
        '/api/implement',
        {
          step,
          processContext: typeof processContext === 'string'
            ? processContext
            : JSON.stringify(processContext),
          mapping: stepMapping || {
            primaryCapability: step.primaryCapability,
            toolDisplayName: step.toolDisplayName,
            caseTypeLabel: step.caseTypeLabel,
          },
        },
        '/api/implement/jobs'
      );
      setImplementData(prev => ({ ...prev, [step.id]: data }));
    } catch (err) {
      console.error('Implementation load error:', err);
      setImplError(prev => ({ ...prev, [step.id]: err.message }));
    }
    setLoadingImpl(null);
  }, [processContext, mappingData]);

  // Handle node click — load implementation (only in "full" mode)
  const onNodeClick = useCallback((event, node) => {
    if (mode === 'preview') return;
    const step = node.data;
    setSelectedStep(step);
    if (!implementData[step.id]) loadImplementation(step);
  }, [mode, implementData, loadImplementation]);

  const handleAddStep = useCallback(() => {
    const newId = `step-${nodes.length + 1}`;
    const lastNode = nodes[nodes.length - 1];
    const newNode = {
      id: newId,
      type: 'processNode',
      position: { x: (lastNode?.position?.x || 0) + 340, y: 180 },
      data: {
        id: newId, name: 'Neuer Schritt', description: 'Klicke um zu bearbeiten',
        type: 'transform', isBottleneck: false,
        primaryCapability: null, toolRecommendation: null,
        toolDisplayName: null, toolDescription: null,
        caseType: null, caseTypeLabel: null,
        isLiteAgent: false, liteAgentRank: null, liteAgentSuggested: false,
      },
    };
    setNodes(nds => [...nds, newNode]);
    if (lastNode) {
      setEdges(eds => [...eds, {
        id: `edge-to-${newId}`, source: lastNode.id, target: newId,
        animated: true, style: { stroke: '#555', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#555' },
      }]);
    }
  }, [nodes, setNodes, setEdges]);

  const handleDeleteSelected = useCallback(() => {
    setNodes(nds => nds.filter(n => !n.selected));
    setEdges(eds => eds.filter(e => !e.selected));
  }, [setNodes, setEdges]);

  const stats = useMemo(() => processData?.overallSummary || null, [processData]);

  const isPreview = mode === 'preview';

  return (
    <div className="canvas-container">
      <div className="canvas-main">
        {/* Top Bar — KPIs only in full mode */}
        {!isPreview && (
          <div className="canvas-top-bar">
            <div className="top-bar-kpis">
              <div className="kpi-item">
                <span className="kpi-number">{processData?.steps?.length || 0}</span>
                <span className="kpi-label">Schritte</span>
              </div>
              <div className="kpi-item highlight">
                <span className="kpi-number">{topLiteAgentIds.length}</span>
                <span className="kpi-label">Lite Agent Kandidaten</span>
              </div>
              {stats?.automationPotential && (
                <div className="kpi-item">
                  <span className="kpi-number">{stats.automationPotential === 'hoch' ? 'Hoch' : stats.automationPotential === 'mittel' ? 'Mittel' : 'Niedrig'}</span>
                  <span className="kpi-label">Automatisierungspotenzial</span>
                </div>
              )}
            </div>
            <div className="canvas-hint">
              Klick auf eine Kachel für Details. Umrandete Kacheln sind Lite-Agent-Kandidaten; die Top-Empfehlungen sind als 'Empfohlener Lite Agent' gekennzeichnet.
            </div>
          </div>
        )}

        <ReactFlow
          nodes={nodes} edges={edges}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          onConnect={onConnect} onNodeClick={onNodeClick}
          nodeTypes={nodeTypes} fitView fitViewOptions={{ padding: 0.3 }}
          defaultEdgeOptions={{ animated: true, style: { stroke: '#555', strokeWidth: 2 } }}
          nodesDraggable={false}
          nodesConnectable={false}
        >
          <Controls />
          <Background color="#333" gap={20} size={1} />
        </ReactFlow>

        {/* Centered mapping button below the process flow (preview mode only) */}
        {isPreview && onStartMapping && (
          <div className="canvas-bottom-action">
            <button className="btn btn-mapping-start" onClick={onStartMapping}>
              KI-Fähigkeiten zuordnen
            </button>
          </div>
        )}
        {/* Action buttons in full mode */}
        {!isPreview && (onExport || onNewProcess) && (
          <div className="canvas-bottom-action" style={{ display: 'flex', gap: '12px', justifyContent: 'center', padding: '12px 0' }}>
            {onExport && (
              <button
                className="btn btn-generate"
                onClick={onExport}
                disabled={exportState === 'loading'}
                style={{
                  background: exportState === 'done' ? '#2e7d32' : exportState === 'loading' ? '#999' : '#2E7C99',
                  color: '#fff',
                  borderColor: exportState === 'done' ? '#2e7d32' : exportState === 'loading' ? '#999' : '#2E7C99',
                  cursor: exportState === 'loading' ? 'not-allowed' : 'pointer',
                  transition: 'background 0.3s',
                }}
              >
                {exportState === 'loading' ? 'Wird erstellt…' : exportState === 'done' ? '✓ Heruntergeladen' : '↓ Ergebnisse exportieren'}
              </button>
            )}
            {onNewProcess && (
              <button className="btn btn-mapping-start" onClick={onNewProcess}>
                + Neuer Prozess
              </button>
            )}
          </div>
        )}
      </div>

      {/* Implementation panel — only in full mode (resizable) */}
      {!isPreview && (
        <>
          <div
            className="panel-resize-handle"
            onMouseDown={handleResizeStart}
            style={{ right: panelWidth - 3 }}
          />
          <div style={{ width: panelWidth, flexShrink: 0 }}>
            <ImplementPanel
              step={selectedStep}
              implementation={selectedStep ? implementData[selectedStep.id] : null}
              isLoading={loadingImpl === selectedStep?.id}
              error={selectedStep ? implError[selectedStep.id] : null}
              onRetry={() => selectedStep && loadImplementation(selectedStep)}
              onClose={() => setSelectedStep(null)}
              mappingData={mappingData}
              processName={processData?.processName}
            />
          </div>
        </>
      )}
    </div>
  );
}
